import { List } from 'immutable';
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router';
import { getShortId } from '@mapado/js-component';
import { MpdSingleSelect, SingleSelectOption } from '@mapado/makeup';
import { OutletContextType } from './DemoContent';
import { SeatConfigShape } from './DemoTypes';

function getLabelFromSeatConfig(seatConfig: SeatConfigShape): string {
  const contractShortId = seatConfig.contract['@id'].replace(
    '/v1/contracts/',
    ''
  );
  const shortId = seatConfig['@id'].replace('/v1/seat_configs/', '');

  return `[${contractShortId}] ${seatConfig.contract.companyDisplayName} | [${shortId}] ${seatConfig.label} (${seatConfig.totalStock} sièges)`;
}

interface Props {
  onChange: (newValue: SingleSelectOption | null) => void;
  seatConfigList: List<SeatConfigShape>;
  selectedSeatConfig: undefined | SeatConfigShape;
}

function SeatConfigSelector({
  onChange,
  seatConfigList,
  selectedSeatConfig = undefined,
}: Props): JSX.Element {
  const options: Array<SingleSelectOption> = seatConfigList
    .map((seatConfig): SingleSelectOption => {
      return {
        label: getLabelFromSeatConfig(seatConfig),
        value: seatConfig['@id'],
      };
    })
    .sortBy((option) => option.label)
    .toArray();

  return (
    <MpdSingleSelect
      placeholder={
        selectedSeatConfig
          ? getLabelFromSeatConfig(selectedSeatConfig)
          : undefined
      }
      options={options}
      onChange={onChange}
      searchable
    />
  );
}

function handleSeatConfigSelectorChange(
  newValue: SingleSelectOption | null,
  location: Location,
  navigate: NavigateFunction
): void {
  if (!newValue) {
    return;
  }

  const selectedSeatConfigId = newValue.value;

  const searchParams = new URLSearchParams(location.search);

  searchParams.set(
    'seatConfigId',
    selectedSeatConfigId ? getShortId(selectedSeatConfigId) : ''
  );

  navigate({
    search: searchParams.toString(),
  });
}

// eslint-disable-next-line react/no-multi-comp
function SeatConfigSelectorContainer(): null | JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { seatConfigList, selectedSeatConfig } =
    useOutletContext<OutletContextType>();

  if (seatConfigList.size === 0) {
    return null;
  }

  return (
    <div className="mb2">
      <SeatConfigSelector
        onChange={(event) =>
          handleSeatConfigSelectorChange(event, location, navigate)
        }
        seatConfigList={seatConfigList}
        selectedSeatConfig={selectedSeatConfig}
      />
    </div>
  );
}

export default SeatConfigSelectorContainer;
